import { supabase } from './SupabaseConfig';
import { NotificationRepository, FcmToken, NotificationRequest, BroadcastMessage } from '../../domain/notification/NotificationRepository';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabaseNotificationRepository implements NotificationRepository {
  async saveToken(userId: string, tokenData: FcmToken): Promise<void> {
    try {
      const { error } = await supabase
        .from('fcm_tokens')
        .upsert({
          owner_id: userId,
          token: tokenData.token,
          user_agent: tokenData.userAgent,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'token'
        });

      if (error) throw new InfrastructureError(`Failed to save FCM token: ${error.message}`, 'SupabaseNotificationRepository');
    } catch (err) {
      console.error('[SupabaseNotificationRepository] saveToken error:', err);
      throw err;
    }
  }

  async enqueueRequest(userId: string, request: NotificationRequest): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('notification_requests')
        .insert({
          owner_id: userId,
          type: request.type,
          payload: request.payload,
          sent: false,
          created_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) throw new InfrastructureError(`Failed to enqueue notification: ${error.message}`, 'SupabaseNotificationRepository');
      return data.id;
    } catch (err) {
      console.error('[SupabaseNotificationRepository] enqueueRequest error:', err);
      throw err;
    }
  }

  async sendBroadcast(userId: string, broadcast: Omit<BroadcastMessage, 'createdAt'>): Promise<void> {
    try {
      const { error } = await supabase
        .from('broadcasts')
        .upsert({
          owner_id: userId,
          message: broadcast.message,
          type: broadcast.type,
          target_location_id: broadcast.targetLocationId,
          created_at: new Date().toISOString()
        }, { onConflict: 'owner_id' }); // Only keep the latest broadcast

      if (error) throw new InfrastructureError(`Failed to send broadcast: ${error.message}`, 'SupabaseNotificationRepository');
    } catch (err) {
      console.error('[SupabaseNotificationRepository] sendBroadcast error:', err);
      throw err;
    }
  }

  listenBroadcast(userId: string, callback: (broadcast: BroadcastMessage | null) => void): () => void {
    const channel = supabase
      .channel(`broadcast_${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'broadcasts',
        filter: `owner_id=eq.${userId}`
      }, async (payload: any) => {
        if (payload.new) {
          callback({
            message: payload.new.message,
            type: payload.new.type,
            targetLocationId: payload.new.target_location_id,
            createdAt: payload.new.created_at
          });
        } else {
          callback(null);
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Initial fetch
          const { data, error } = await supabase
            .from('broadcasts')
            .select('*')
            .eq('owner_id', userId)
            .maybeSingle();
          
          if (data && !error) {
            callback({
              message: data.message,
              type: data.type,
              targetLocationId: data.target_location_id,
              createdAt: data.created_at
            });
          }
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }
}
